import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/d318c9c3-21c1-4ea6-9834-c444788acba2/images/c3d7f395d9b6a31656e0342a0bdcd27207c485b4eca8a43a4bd19dc3f36a6145.png";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name, referralCode);
      nav("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-enter min-h-[calc(100vh-4rem)] grid lg:grid-cols-2 bg-[#FAFAF7]">
      <div className="relative hidden lg:block overflow-hidden warm-bg border-r border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <Sparkles className="w-10 h-10 text-[#E11D48] mb-6" />
          <h2 className="font-heading font-extrabold text-5xl tracking-tight leading-tight mb-3 text-slate-900">{t("auth.register.subtitle_1")}<br />{t("auth.register.subtitle_2")}</h2>
          <p className="text-slate-600 max-w-md leading-relaxed">{t("auth.register.subtitle_desc")}</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="register-form">
          <div className="text-xs font-semibold text-[#E11D48] uppercase tracking-wider mb-2">{t("auth.register.label")}</div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight text-slate-900 mb-8">{t("auth.register.title")}</h1>

          <label className="block mb-4">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">{t("auth.register.name")}</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" className="w-full" placeholder="Ash Ketchum" />
          </label>
          <label className="block mb-4">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">{t("auth.register.email")}</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input" className="w-full" placeholder="you@example.com" />
          </label>
          <label className="block mb-4">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">{t("auth.register.password")}</span>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password-input" className="w-full" minLength={6} placeholder="••••••••" />
          </label>
          <label className="block mb-6">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">{t("auth.register.referral_code")}</span>
            <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} data-testid="register-referral-input" className="w-full font-mono tracking-wider" placeholder={t("auth.register.referral_placeholder")} maxLength={16} />
          </label>

          {error && <div className="text-[#E11D48] text-sm mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg" data-testid="register-error">{error}</div>}

          <button type="submit" disabled={loading} data-testid="register-submit-btn" className="btn-primary w-full">
            {loading ? t("auth.register.creating") : t("auth.register.create")}
          </button>

          <p className="text-sm text-slate-500 mt-6 text-center">
            {t("auth.register.have_account")}{" "}
            <Link to="/login" data-testid="register-to-login-link" className="text-[#E11D48] font-semibold hover:underline">{t("auth.register.log_in")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
